import { ParamsType } from "@ant-design/pro-provider";
import ProTable, { ActionType, ProColumns } from "@ant-design/pro-table";
import "@ant-design/pro-table/dist/table.less";
import { ProSchema } from "@ant-design/pro-utils/lib/typing";
import { RecordKey } from "@ant-design/pro-utils/lib/useEditableArray";
import { Button, Input, InputRef, Typography } from "antd";
import _ from "lodash";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  NEW_OPERATOR_SENTINEL,
  OPERATOR_PERMISSION_SCOPES,
  Operator,
} from "../../../../types";
import AntDeletePopupConfirm from "../../../common/AntDeletePopupConfirm";
import useSearchHotkey from "../../../hooks/useSearchHotkey";
import { FIRM_LABELS, Firm } from "@/modules/firms";

const PERMISSION_SCOPE_VALUE_ENUM = OPERATOR_PERMISSION_SCOPES.reduce(
  (acc, scope) => ({ ...acc, [scope]: scope }),
  {} as Record<string, string>
);

const getColumns = (
  onDeleteHandler: (key: string, record: Operator) => void,
  emailRef: RefObject<InputRef>,
  firmLabel: string
): ProColumns<Operator>[] => [
  {
    dataIndex: "email",
    title: "Email Address",
    valueType: "text",
    formItemProps: () => ({
      rules: [
        {
          required: true,
          message: "Please enter operator email",
          type: "email",
        },
      ],
    }),
    renderFormItem: (_: ProSchema<Operator>, config: any) => {
      return (
        <Input
          {...config.fieldProps}
          ref={emailRef}
          placeholder="email@smarta-steuern.de"
        />
      );
    },
    editable: (_, record) => record.id === NEW_OPERATOR_SENTINEL,
    sorter: (a: Operator, b: Operator) => a.email.localeCompare(b.email),
  },
  {
    dataIndex: "displayName",
    title: "Display Name",
    valueType: "text",
    formItemProps: () => ({
      rules: [{ required: true, message: "Please enter the operator's name" }],
    }),
    sorter: (a: Operator, b: Operator) =>
      a.displayName.localeCompare(b.displayName),
  },
  {
    dataIndex: "firm",
    title: firmLabel,
    valueType: "select",
    valueEnum: FIRM_LABELS,
    sorter: (a: Operator, b: Operator) => a.firm.localeCompare(b.firm),
    formItemProps: () => ({
      rules: [{ required: true, message: "Please select a firm" }],
    }),
  },
  {
    dataIndex: "permissionScope",
    title: "Permission Scope",
    valueType: "select",
    valueEnum: PERMISSION_SCOPE_VALUE_ENUM,
    sorter: (a: Operator, b: Operator) =>
      a.permissionScope.localeCompare(b.permissionScope),
    formItemProps: () => ({
      rules: [{ required: true, message: "Please select permission scope" }],
    }),
  },
  {
    title: "Actions",
    valueType: "option",
    render: (_text, record, _, action) => [
      <Typography.Link
        key="editable"
        onClick={() => {
          action?.startEditable?.(record.id);
        }}
      >
        Edit
      </Typography.Link>,
      <AntDeletePopupConfirm
        key="deletePopup"
        title="Are you sure to delete this operator?"
        onDelete={() => onDeleteHandler(record.id, record)}
      >
        <Typography.Link key="deleteLink">Delete</Typography.Link>
      </AntDeletePopupConfirm>,
    ],
  },
];

const PermissionsTable = ({
  operators,
  loading,
  onDelete,
  onSave,
  defaultFirm = "smarta",
}: {
  operators: Array<Operator>;
  loading: boolean;
  onDelete: (operator: Operator) => Promise<void>;
  onSave: (operator: Operator) => Promise<void>;
  defaultFirm?: Firm;
}) => {
  const { t } = useTranslation();
  // Keep editableKeys a stable array — never undefined. ProTable's
  // useEditableArray feeds this controlled prop through rc-util useMergedState,
  // which on a defined→undefined transition syncs its inner value to undefined
  // and then hits an unguarded `editableKeys.map(...)` during render (SMT-969:
  // "Cannot read properties of undefined (reading 'map')"). Initialising to `[]`
  // and resetting to `[]` (not undefined) keeps the prop a stable array.
  const [editableKeys, setEditableRowKeys] = useState<React.Key[]>([]);
  const [dataSource, setDataSource] = useState<Operator[]>([]);
  const emailRef = useRef<InputRef>(null);
  useSearchHotkey(emailRef);
  const actionRef = useRef<ActionType>();

  useEffect(() => {
    setDataSource(operators);
  }, [operators]);

  const onCancelHandler = async () => {
    const newData = _.filter(
      dataSource,
      (op) => op.id !== NEW_OPERATOR_SENTINEL
    );
    if (editableKeys?.length) {
      actionRef.current?.cancelEditable(editableKeys[0]);
    }
    setDataSource(newData);
    setEditableRowKeys([]);
  };

  const onCreateHandler = () => {
    const draft: Operator = {
      id: NEW_OPERATOR_SENTINEL,
      identityId: "",
      email: "",
      displayName: "",
      permissionScope: "ADMIN",
      firm: defaultFirm,
      createdAt: 0,
      updatedAt: 0,
    };
    actionRef.current?.addEditRecord(draft, { position: "top" });
  };

  const onDeleteHandler = async (_key: RecordKey, record: Operator) => {
    setEditableRowKeys([]);
    await onDelete(record);
  };

  const onFilterHandler = (params: ParamsType) => {
    const filterPredicates: Array<((op: Operator) => boolean) | undefined> = [
      params.email
        ? (d: Operator) =>
            d.email.toLowerCase().includes(params.email.toLowerCase())
        : undefined,
      params.displayName
        ? (d: Operator) =>
            d.displayName
              .toLowerCase()
              .includes(params.displayName.toLowerCase())
        : undefined,
      params.permissionScope
        ? (d: Operator) => d.permissionScope === params.permissionScope
        : undefined,
      params.firm ? (d: Operator) => d.firm === params.firm : undefined,
    ];

    const filtered = operators.filter((op) =>
      filterPredicates.every((predicate) => !predicate || predicate(op))
    );

    setDataSource(filtered);
  };

  const onResetFilterHandler = () => {
    setDataSource(operators);
  };

  const onSaveHandler = async (key: RecordKey, record: Operator) => {
    const item = dataSource.find((op) => op.id === key);
    const next = { ...item, ...record } as Operator;
    // Persist upstream and let the parent refresh `operators` (which drives
    // `dataSource` via the effect). ProTable exits edit mode once this
    // resolves and keeps the row editable if it rejects — so we must NOT
    // mutate `dataSource`/`editableKeys` here, or the editable row desyncs
    // from ProTable and the post-save re-render throws.
    await onSave(next);
  };

  const onUpdateHandler = (keys: React.Key[]) => {
    setEditableRowKeys(keys);
  };

  return (
    <ProTable<Operator>
      loading={loading}
      rowKey="id"
      actionRef={actionRef}
      columns={getColumns(onDeleteHandler, emailRef, t("firm"))}
      dataSource={dataSource}
      headerTitle="Operators"
      toolBarRender={() => [
        <Button
          key="button"
          type="primary"
          style={{ float: "right" }}
          disabled={!!editableKeys?.length}
          onClick={onCreateHandler}
        >
          Add new
        </Button>,
      ]}
      search={{
        filterType: "query",
        labelWidth: "auto",
        searchText: "Search",
      }}
      onSubmit={onFilterHandler}
      onReset={onResetFilterHandler}
      onChange={onCancelHandler}
      cardBordered={true}
      editable={{
        type: "single",
        deletePopconfirmMessage: "Are you sure to delete this operator?",
        editableKeys,
        onlyAddOneLineAlertMessage:
          "Please Save or Cancel current updatable row",
        onlyOneLineEditorAlertMessage:
          "Please Save or Cancel current updatable row",
        onCancel: onCancelHandler,
        onChange: onUpdateHandler,
        onSave: onSaveHandler,
        actionRender: (_row, _config, defaultDoms) => {
          return [defaultDoms.save, defaultDoms.cancel];
        },
      }}
      toolbar={{ settings: [] }}
      pagination={{ pageSize: 10, showSizeChanger: false }}
    ></ProTable>
  );
};

export default PermissionsTable;
